import { z } from "zod";

/**
 * The uniform error body the API's global exception filter emits. This is the
 * producer's contract: `filters.ts` asserts its response against it, so a
 * rename here fails the backend build rather than silently reaching consumers.
 */
export const ApiErrorEnvelopeSchema = z.object({
  statusCode: z.number(),
  error: z.string(),
  message: z.string(),
  code: z.string().optional(),
  data: z.record(z.string(), z.unknown()).optional(),
  errorRef: z.string().optional(),
});

export type ApiErrorEnvelope = z.infer<typeof ApiErrorEnvelopeSchema>;

const shape = ApiErrorEnvelopeSchema.shape;

function tolerant<T extends z.ZodType>(field: T): z.ZodCatch<z.ZodOptional<T>> {
  return field.optional().catch(undefined);
}

/**
 * The consumer's view of the same contract, for parsing an untrusted response.
 * Every field is derived from the producer's shape above, so a rename there
 * breaks this too instead of leaving a consumer reading a key that no longer
 * exists. Deliberately more forgiving at runtime: a body may be partial, and a
 * malformed field falls back to `undefined` rather than failing the parse, so
 * it cannot hide its healthy siblings — a Nest exception whose `message` is an
 * array must still surrender its `code`. A non-object body still fails.
 */
export const ApiErrorEnvelopeReadSchema = z.looseObject({
  statusCode: tolerant(shape.statusCode),
  error: tolerant(shape.error),
  message: tolerant(shape.message),
  code: tolerant(shape.code),
  data: tolerant(shape.data),
  errorRef: tolerant(shape.errorRef),
});

export type ApiErrorEnvelopeRead = z.infer<typeof ApiErrorEnvelopeReadSchema>;
